import {
  Button,
  DEFAULT_MAP_ID,
  MAX_SHOT_DISTANCE,
  PLAYER_MAX_HEALTH,
  Rng,
  TICK_DT,
  WEAPONS,
  copy,
  emptyEvents,
  fireIntervalMs,
  getMap,
  hasButton,
  set,
  vec3,
  weaponFromIndex,
  type GameMap,
  type InputCommand,
  type PlayerId,
  type PlayerState,
  type SimEvents,
  type TeamId,
  type WeaponId,
} from '@game/shared';
import { CollisionWorld } from './collision.js';
import {
  applySpread,
  hitboxForPlayer,
  shotDamage,
  traceShot,
  type PlayerHitbox,
} from './combat.js';
import { eyePosition, playerHeight, stepMovement } from './movement.js';

const scratchOrigin = vec3();
const scratchDirection = vec3();

/**
 * The full game simulation. One instance per match on the server; one instance
 * on each client for prediction. Time is derived from `tick` alone so that
 * re-simulating the same ticks with the same inputs always yields the same state.
 */
export class GameSim {
  map: GameMap;
  collision: CollisionWorld;
  readonly players = new Map<PlayerId, PlayerState>();

  tick = 0;
  /** Set true on the client: suppresses authoritative-only effects like damage. */
  predicting = false;

  private spawnCursor = 0;

  constructor(mapId: string = DEFAULT_MAP_ID) {
    this.map = getMap(mapId);
    this.collision = new CollisionWorld(this.map);
  }

  /**
   * Swaps the map in place, keeping every connected player. Called on the server
   * when a match rotates and on the client when the server announces a different
   * map than the one prediction is running — both sides then rebuild collision
   * from the same brush list, which is what keeps prediction valid.
   */
  loadMap(mapId: string): void {
    if (mapId === this.map.id) return;
    this.map = getMap(mapId);
    this.collision = new CollisionWorld(this.map);
    this.spawnCursor = 0;
    for (const player of this.players.values()) this.respawn(player);
  }

  /** Simulation clock in milliseconds. Derived from the tick, never from Date.now(). */
  get timeMs(): number {
    return this.tick * TICK_DT * 1000;
  }

  createPlayer(id: PlayerId, team: TeamId, weapon: WeaponId = 'smg'): PlayerState {
    const player: PlayerState = {
      id,
      team,
      position: vec3(),
      velocity: vec3(),
      yaw: 0,
      pitch: 0,
      health: PLAYER_MAX_HEALTH,
      alive: true,
      onGround: false,
      crouching: false,
      sliding: false,
      slideTimerMs: 0,
      slideCooldownMs: 0,
      slideAirMs: 0,
      coyoteMs: 0,
      jumpBufferMs: 0,
      wasJumpDown: false,
      wasCrouchDown: false,
      weapon,
      ammo: WEAPONS[weapon].magazine,
      magazines: {},
      reloadEndsAtMs: 0,
      nextShotAtMs: 0,
      spread: WEAPONS[weapon].spreadMin,
      kills: 0,
      deaths: 0,
    };
    this.players.set(id, player);
    this.respawn(player);
    return player;
  }

  removePlayer(id: PlayerId): void {
    this.players.delete(id);
  }

  /**
   * Starts a fresh round: everyone alive at a spawn. Weapons are left as they
   * are so the caller can apply loadout choices first. Kills and deaths are
   * per-game, not per-round — pass `clearScores` only when a new game begins.
   */
  resetRound(clearScores = true): void {
    this.spawnCursor = 0;
    for (const player of this.players.values()) {
      if (clearScores) {
        player.kills = 0;
        player.deaths = 0;
      }
      this.respawn(player);
    }
  }

  /** Picks the spawn point of the player's team that is furthest from any enemy. */
  respawn(player: PlayerState): void {
    const candidates = this.map.spawns.filter((s) => s.team === player.team);
    const pool = candidates.length > 0 ? candidates : this.map.spawns;

    let best = pool[this.spawnCursor % pool.length]!;
    let bestScore = -Infinity;
    for (const spawn of pool) {
      let score = Infinity;
      for (const other of this.players.values()) {
        if (other.id === player.id || other.team === player.team || !other.alive)
          continue;
        const dx = other.position.x - spawn.position.x;
        const dz = other.position.z - spawn.position.z;
        score = Math.min(score, dx * dx + dz * dz);
      }
      if (score > bestScore) {
        bestScore = score;
        best = spawn;
      }
    }
    this.spawnCursor++;

    copy(player.position, best.position);
    set(player.velocity, 0, 0, 0);
    player.yaw = best.yaw;
    player.pitch = 0;
    player.health = PLAYER_MAX_HEALTH;
    player.alive = true;
    player.onGround = false;
    player.crouching = false;
    player.sliding = false;
    player.ammo = WEAPONS[player.weapon].magazine;
    player.magazines = {};
    player.reloadEndsAtMs = 0;
    player.nextShotAtMs = this.timeMs;
    player.spread = WEAPONS[player.weapon].spreadMin;
  }

  /** Hitboxes of everyone the shooter is allowed to hit. */
  enemyHitboxes(shooter: PlayerState, friendlyFire = false): PlayerHitbox[] {
    const out: PlayerHitbox[] = [];
    for (const other of this.players.values()) {
      if (other.id === shooter.id || !other.alive) continue;
      if (!friendlyFire && other.team === shooter.team) continue;
      out.push(hitboxForPlayer(other));
    }
    return out;
  }

  /**
   * Advances one player by one tick: movement, then weapon handling. Pass the
   * hitboxes to trace against — the server supplies rewound ones for lag
   * compensation, the client supplies none while predicting.
   */
  stepPlayer(
    player: PlayerState,
    input: InputCommand,
    events: SimEvents,
    hitboxes: readonly PlayerHitbox[] = [],
  ): void {
    if (!player.alive) {
      // Rounds are elimination: the dead stay down until `resetRound`, free to
      // ghost-walk and watch the rest of the fight (invisible, no hitbox, no
      // footsteps). Clamp them at the kill plane — an eliminated player falling
      // for the rest of a round would overflow the quantised wire position.
      stepMovement(this.collision, player, input, TICK_DT);
      if (player.position.y < this.collision.killPlaneY) {
        player.position.y = this.collision.killPlaneY;
        set(player.velocity, 0, 0, 0);
      }
      return;
    }

    stepMovement(this.collision, player, input, TICK_DT);

    if (player.position.y < this.collision.killPlaneY) {
      // In the lobby, falling out of bounds is a teleport home, not a death.
      if (this.map.combat) this.killPlayer(player, null);
      else this.respawn(player);
      return;
    }

    // Weapons are dead in the lobby. Both sides skip identically because the
    // flag comes from the map itself — prediction cannot drift on this.
    if (!this.map.combat) return;

    this.stepWeapon(player, input, events, hitboxes);
  }

  private stepWeapon(
    player: PlayerState,
    input: InputCommand,
    events: SimEvents,
    hitboxes: readonly PlayerHitbox[],
  ): void {
    const now = this.timeMs;
    const requested = weaponFromIndex(input.weapon);
    if (requested !== player.weapon && player.reloadEndsAtMs === 0) {
      // The magazine travels with its weapon: stash what is left in the one
      // being holstered and draw the other exactly as it was left, so cycling
      // weapons is never a free reload. Only respawn refills.
      player.magazines[player.weapon] = player.ammo;
      player.weapon = requested;
      player.ammo = player.magazines[requested] ?? WEAPONS[requested].magazine;
      player.nextShotAtMs = now + 250;
      player.spread = WEAPONS[requested].spreadMin;
    }

    const def = WEAPONS[player.weapon];

    // Spread recovers toward its resting value every tick.
    player.spread = Math.max(
      def.spreadMin,
      player.spread - def.spreadRecovery * TICK_DT,
    );

    if (player.reloadEndsAtMs > 0) {
      if (now >= player.reloadEndsAtMs) {
        player.ammo = def.magazine;
        player.reloadEndsAtMs = 0;
      }
      return;
    }

    const wantsReload = hasButton(input.buttons, Button.Reload) || player.ammo <= 0;
    if (wantsReload && player.ammo < def.magazine) {
      player.reloadEndsAtMs = now + def.reloadMs;
      return;
    }

    if (!hasButton(input.buttons, Button.Fire)) return;
    if (now < player.nextShotAtMs || player.ammo <= 0) return;

    player.ammo--;
    player.nextShotAtMs = now + fireIntervalMs(def);

    // Seeded per shot so client prediction and server authority agree.
    const rng = new Rng((player.id * 0x9e3779b1) ^ (this.tick * 0x85ebca6b));
    eyePosition(player, scratchOrigin);

    for (let pellet = 0; pellet < def.pellets; pellet++) {
      applySpread(scratchDirection, player.yaw, player.pitch, player.spread, rng);
      const result = traceShot(
        this.collision,
        scratchOrigin,
        scratchDirection,
        hitboxes,
        MAX_SHOT_DISTANCE,
      );

      events.shots.push({
        shooter: player.id,
        origin: vec3(scratchOrigin.x, scratchOrigin.y, scratchOrigin.z),
        end: result.end,
        weapon: player.weapon,
      });

      if (result.victim === null || this.predicting) continue;
      const victim = this.players.get(result.victim);
      if (!victim || !victim.alive) continue;

      const damage = shotDamage(def, result.distance, result.headshot);
      victim.health -= damage;
      const killed = victim.health <= 0;

      events.hits.push({
        attacker: player.id,
        victim: victim.id,
        damage,
        headshot: result.headshot,
        killed,
        point: result.end,
      });

      if (killed) this.killPlayer(victim, player);
    }

    player.spread = Math.min(def.spreadMax, player.spread + def.spreadPerShot);
  }

  /** No respawn timer: death is elimination, undone only by `resetRound`. */
  killPlayer(victim: PlayerState, attacker: PlayerState | null): void {
    if (!victim.alive) return;
    victim.alive = false;
    victim.health = 0;
    victim.deaths++;
    victim.sliding = false;
    victim.crouching = false;
    set(victim.velocity, 0, 0, 0);
    if (attacker && attacker.id !== victim.id) attacker.kills++;
  }

  /** Convenience for single-player / offline testing. */
  stepAll(inputs: Map<PlayerId, InputCommand>): SimEvents {
    const events = emptyEvents();
    this.tick++;
    for (const player of this.players.values()) {
      const input = inputs.get(player.id);
      if (!input) continue;
      this.stepPlayer(player, input, events, this.enemyHitboxes(player));
    }
    return events;
  }
}

export { playerHeight };
